import { useEffect } from 'react';

/**
 * SplashScreen Component
 * Handles hiding the inline splash screen that appears only on initial PWA launch
 * - Only shows in standalone PWA mode
 * - Does not show on refresh
 * - Does not show in regular browser
 */
const SplashScreen = () => {
  useEffect(() => {
    // Check if splash screen should be shown
    const inlineSplash = document.getElementById('inline-splash-screen');
    
    // If splash is not visible (not PWA or refresh), do nothing
    if (!inlineSplash || inlineSplash.style.display === 'none') {
      return;
    }

    // Hide splash screen after a minimum display time and when app is ready
    const minDisplayTime = 2500; // Minimum 2.5 seconds for better UX and brand visibility
    const fadeOutDuration = 500; // Smooth 500ms fade out transition
    const startTime = Date.now();

    const hideSplash = () => {
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);
      
      setTimeout(() => {
        // Fade out and remove the inline splash screen
        if (inlineSplash) {
          inlineSplash.classList.add('fade-out');
          // Show root content after fade out starts
          setTimeout(() => {
            const root = document.getElementById('root');
            if (root) {
              root.style.display = 'block';
              root.style.visibility = 'visible';
              root.style.opacity = '1';
            }
            // Remove splash screen after fade out completes
            inlineSplash.remove();
          }, fadeOutDuration);
        }
      }, remainingTime);
    };

    // Fallback: Ensure root is visible after 6 seconds even if splash doesn't hide
    const fallbackTimeout = setTimeout(() => {
      const root = document.getElementById('root');
      if (root && root.style.display === 'none') {
        console.warn('Fallback: Showing root after timeout');
        root.style.display = 'block';
        root.style.visibility = 'visible';
        root.style.opacity = '1';
      }
    }, 6000);

    // Hide splash when DOM is ready
    if (document.readyState === 'complete') {
      hideSplash();
    } else {
      window.addEventListener('load', hideSplash);
      return () => {
        clearTimeout(fallbackTimeout);
        window.removeEventListener('load', hideSplash);
      };
    }
  }, []);

  // This component doesn't render anything - it just handles hiding the inline splash
  return null;
};

export default SplashScreen;

